import React, { useState, useRef, useMemo } from "react";
import * as tus from "tus-js-client";
import { useForm } from "react-hook-form";
import {
  Box,
  Button,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
  CircularProgress,
  GlobalStyles,
  useTheme,
  useMediaQuery
} from "@mui/material";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import { frFR, enUS } from "@mui/x-data-grid/locales";
import { Add, Close, CloudUpload } from "@mui/icons-material";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useEffect } from "react";
import {
  fetchAudioLibraries,
  // createAudioLibrary,
  fetchAudioLibraryById,
  // updateAudioLibrary,
  deleteAudioLibrary
} from "../../Redux/Reducers/AudioLibrariesReducer";
import toast from "react-hot-toast";
import { formatUTCDateForDataGrid } from "../../utils/dateUtils";
import usePermissions from "../../hooks/usePermissions";
import { getFullUrl } from "../../utils/globalUtils";
import { CustomLoadingOverlay } from "../../components/common/TableLoader";


const MODULE_KEY = 'audioLibrary';

const MAX_FILES = 10;
const MAX_FILE_SIZE = 100 * 1024 * 1024; // 100 MB
const MAX_TOTAL_SIZE = 500 * 1024 * 1024; // 500 MB
const MIN_DURATION = 5; // seconds
const MAX_DURATION = 7200; // 2 hours

const ALLOWED_TYPES = [
  "audio/mpeg",
];

const ALLOWED_EXTENSIONS = ["mp3"];

function AudioLibrary({ onSelect }) {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const { hasAction } = usePermissions();

  const [modalOpen, setModalOpen] = useState(false);
  const [viewModalOpen, setViewModalOpen] = useState(false);
  const { audios = [], loading, pagination } = useSelector((state) => state.audios || {});
  const [searchText, setSearchText] = useState("");

  const [selectedFiles, setSelectedFiles] = useState([]);
  const [audioPreview, setAudioPreview] = useState(null);
  const [selectedAudio, setSelectedAudio] = useState(null);
  const [uploadProgress, setUploadProgress] = useState({});
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [isUploading, setIsUploading] = useState(false);

  const fileInputRef = useRef(null);

  const [paginationModel, setPaginationModel] = useState({
    page: 0,
    pageSize: 10,
  });

  // For Table Thead Width
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xl'));
  const isLarge = useMediaQuery(theme.breakpoints.up('xl'));

  useEffect(() => {
    dispatch(
      fetchAudioLibraries({
        page: paginationModel.page + 1,
        limit: paginationModel.pageSize,
        search: searchText,
      })
    );
  }, [paginationModel, searchText, dispatch]);

  const {
    // control,
    // handleSubmit,
    // watch,
    reset,
    // setValue,
    // formState: { errors }
  } = useForm({
    defaultValues: {
      audios
    }
  });


  useEffect(() => {
    return () => {
      selectedFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });
    };
  }, [selectedFiles]);

  const validateAudioDuration = (file) => {
    return new Promise((resolve, reject) => {
      const audio = document.createElement("audio");

      audio.preload = "metadata";

      audio.onloadedmetadata = () => {
        URL.revokeObjectURL(audio.src);

        const duration = audio.duration;

        if (duration < MIN_DURATION) {
          reject(
            `${file.name} duration should be at least ${MIN_DURATION} seconds`
          );
        } else if (duration > MAX_DURATION) {
          reject(
            `${file.name} exceeds maximum duration of ${MAX_DURATION / 3600
            } hours`
          );
        } else {
          resolve(true);
        }
      };

      audio.onerror = () => {
        reject(`${file.name} is not a valid audio file`);
      };

      audio.src = URL.createObjectURL(file);
    });
  };

  const validateFile = async (file, existingFiles = []) => {

    // Empty file validation
    if (file.size === 0) {
      throw new Error(`${file.name} is empty or corrupted`);
    }

    // File type validation
    if (!ALLOWED_TYPES.includes(file.type)) {
      throw new Error(`${file.name} is not a valid MP3 file`);
    }

    // Extension validation
    const extension = file.name.split(".").pop()?.toLowerCase();

    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      throw new Error(`${file.name} must have .mp3 extension`);
    }

    // File size validation
    if (file.size > MAX_FILE_SIZE) {
      throw new Error(
        t("audio_library.validations.audio_size_limit")
      );
    }
    // if (file.size > MAX_FILE_SIZE) {
    //   throw new Error(
    //     `${file.name} exceeds ${(MAX_FILE_SIZE / 1024 / 1024).toFixed(0)} MB limit`
    //   );
    // }

    // Filename length validation
    if (file.name.length > 100) {
      throw new Error(`${file.name} filename is too long`);
    }

    // Invalid characters validation
    const invalidChars = /[<>:"/\\|?*]/;

    if (invalidChars.test(file.name)) {
      throw new Error(`${file.name} contains invalid characters`);
    }

    // Duplicate validation
    const isDuplicate = existingFiles.some(
      (f) => f.name === file.name && f.size === file.size
    );

    if (isDuplicate) {
      throw new Error(`${file.name} already selected`);
    }

    // Audio duration validation
    await validateAudioDuration(file);

    return true;
  };

  const handleSubmitForm = async () => {

    if (selectedFiles.length === 0) {
      toast.error(
        t(
          "audio_library.validations.audio_required",
          "Please select at least one audio file"
        )
      );
      return;
    }

    if (!navigator.onLine) {
      toast.error("No internet connection");
      return;
    }

    setIsUploading(true);

    try {

      // Final validation before upload
      await Promise.all(
        selectedFiles.map((file) =>
          validateFile(file)
        )
      );

      const uploadPromises = selectedFiles.map((file) => {

        return new Promise((resolve, reject) => {

          const upload = new tus.Upload(file, {

            endpoint: process.env.REACT_APP_TUS_ENDPOINT,

            retryDelays: [0, 3000, 5000, 10000],

            headers: {
              Authorization: `Bearer ${sessionStorage.getItem(
                "admin-token"
              )}`,
            },

            metadata: {
              filename: file.name,
              filetype: file.type,
            },

            onError: function (error) {

              console.error("Upload failed:", error);

              toast.error(`Upload failed for ${file.name}`);

              reject(error);
            },

            onProgress: (bytesUploaded, bytesTotal) => {

              const percentage = Math.floor(
                (bytesUploaded / bytesTotal) * 100
              );

              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: percentage,
              }));
            },

            onSuccess: function () {

              console.log("Upload finished:", upload.url);

              if (!upload.url) {
                reject(new Error("Upload URL missing"));
                return;
              }

              setUploadProgress((prev) => ({
                ...prev,
                [file.name]: 100,
              }));

              resolve(upload.url);
            },
          });

          upload.findPreviousUploads().then((previousUploads) => {

            if (previousUploads.length) {
              upload.resumeFromPreviousUpload(previousUploads[0]);
            }

            upload.start();

          }).catch((err) => {
            reject(err);
          });

        });

      });

      await Promise.all(uploadPromises);

      toast.success(
        t(
          "audio_library.messages.upload_success",
          "All audios uploaded successfully"
        )
      );

      // Cleanup previews
      selectedFiles.forEach((file) => {
        if (file.preview) {
          URL.revokeObjectURL(file.preview);
        }
      });

      setSelectedFiles([]);
      setUploadProgress({});
      setModalOpen(false);

      dispatch(
        fetchAudioLibraries({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
          search: searchText,
        })
      );

    } catch (err) {

      console.error(err);

      toast.error(
        err?.message || "Some uploads failed"
      );

    } finally {

      setIsUploading(false);

    }
  };

  const handleFileSelect = async (files) => {

    if (!files || files.length === 0) return;

    // Internet validation
    if (!navigator.onLine) {
      toast.error("No internet connection");
      return;
    }

    const newFiles = Array.from(files);

    // Max file count validation
    if (selectedFiles.length + newFiles.length > MAX_FILES) {
      toast.error(
        t(
          "audio_library.validations.upload_max_limit",
          "You can upload maximum 10 files at a time"
        )
      );
      return;
    }

    // Total upload size validation
    const totalSize = [...selectedFiles, ...newFiles].reduce(
      (sum, file) => sum + file.size,
      0
    );

    if (totalSize > MAX_TOTAL_SIZE) {
      toast.error("Total upload size exceeds 500 MB");
      return;
    }

    const validFiles = [];

    for (const file of newFiles) {
      try {

        await validateFile(file, [...selectedFiles, ...validFiles]);

        file.preview = URL.createObjectURL(file);

        validFiles.push(file);

      } catch (err) {
        toast.error(err.message || err);
      }
    }

    setSelectedFiles((prev) => [...prev, ...validFiles]);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();

    const files = e.dataTransfer.files;
    handleFileSelect(files);
  };

  const handleView = async (rowId = null) => {
    try {
      setSelectedAudio(null);
      setAudioPreview(null);
      setViewModalOpen(true);
      if (rowId) {
        const row = await dispatch(fetchAudioLibraryById(rowId)).unwrap();

        setSelectedAudio(row);
        setAudioPreview(row?.cdnUrl ? getFullUrl(row.cdnUrl, 'audio') : null);
        reset({
          originalName: row.originalName || "",
          durationSeconds: row.durationSeconds || "",
          fileSize: row.fileSize || "",
          createdBy: row.createdByName || "",
        });
      }
    } catch (err) {
      toast.error("Failed to fetch audio details");
    }
  };

  const handleRemoveFile = (index) => {
    setSelectedFiles((prev) => {
      const fileToRemove = prev[index];

      if (fileToRemove?.preview) {
        URL.revokeObjectURL(fileToRemove.preview);
      }

      return prev.filter((_, i) => i !== index);
    });

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleDeleteClick = (id) => {
    setSelectedId(id);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    setDeleting(true);
    try {
      await dispatch(deleteAudioLibrary({ id: selectedId })).unwrap();
      toast.success(t('audio_library.messages.delete_success', "Audio deleted successfully"));
      dispatch(
        fetchAudioLibraries({
          page: paginationModel.page + 1,
          limit: paginationModel.pageSize,
          search: searchText,
        })
      );
    } catch (err) {
      console.log(err, 'err');
      toast.error(t('audio_library.messages.delete_failed', "Failed to delete audio"));
    } finally {
      setDeleting(false);
      setDeleteDialogOpen(false);
      setSelectedId(null);
    }
  };

  const formatDuration = (sec) => {
    if (!sec || sec === 'N/A') return "N/A";
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${s < 10 ? "0" : ""}${s}`;
  };

  const columns = [
    {
      field: 'sno',
      headerName: t('audio_library.fields.sno', 'S.No'),
      width: 80,
      sortable: false,
      renderCell: (params) => {
        const allRows = params.api.getAllRowIds();
        const currentRowIndex = allRows.indexOf(params.id);
        const visibleRowIndex = currentRowIndex % paginationModel.pageSize;
        return paginationModel.page * paginationModel.pageSize + visibleRowIndex + 1;
      }
    },

    {
      field: "originalName",
      headerName: t('audio_library.fields.file_name', "File Name"),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
    },
    {
      field: "durationSeconds",
      headerName: t('audio_library.fields.duration', "Duration"),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
      renderCell: (params) => formatDuration(params.value)
    },
    {
      field: "fileSize",
      headerName: t('audio_library.fields.file_size', "File Size (MB)"),
      ...(isMobile && { width: 200 }),
      ...(isLarge && { flex: 1 }),
      renderCell: (params) =>
        (params.value / (1024 * 1024)).toFixed(2)
    },
    {
      field: "createdByName",
      headerName: t('audio_library.fields.uploaded_by', "Uploaded By"),
      ...(isMobile && { width: 200 }),
      ...(isLarge && { flex: 1 }),
    },
    {
      field: "createdAt",
      headerName: t('audio_library.fields.upload_date', "Upload Date"),
      ...(isMobile && { width: 200 }),
      ...(isLarge && { flex: 1 }),
      valueFormatter: (value) => {
        return formatUTCDateForDataGrid(value);
      }
    },
    {
      field: "usedIn",
      headerName: t('audio_library.fields.used_in', "Used In"),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
      renderCell: (params) => {
        return `${params.value} tracks`;
      }
    },
    {
      field: "status",
      headerName: t('audio_library.fields.status', "Status"),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
      renderCell: (params) =>
        params.value === "ACTIVE" ? "Success" : "Processing"
    },
    {
      field: "fileUploadStatus",
      headerName: t('audio_library.fields.file_status', "File Status"),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
      renderCell: (params) =>
        params.value
    },
    {
      field: "actions",
      headerName: t('audio_library.fields.actions', "Actions"),
      ...(isMobile && { width: 150 }),
      ...(isLarge && { flex: 1 }),
      renderCell: (params) => (
        <>
          <Box sx={{ display: 'flex', gap: '6px', }}>
            {hasAction(MODULE_KEY, 'read') && (
              <IconButton onClick={() => handleView(params.row.id)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="13" viewBox="0 0 18 13" fill="none">
                  <path d="M17.415 4.66071C15.5893 1.74214 12.4457 0 9 0C5.55429 0 2.41071 1.74214 0.585 4.66071L0.437143 4.89857C0.154286 5.36143 0 5.88857 0 6.42857C0 6.96857 0.154286 7.49571 0.437143 7.95857L0.585 8.19643C2.41071 11.115 5.55429 12.8571 9 12.8571C12.4457 12.8571 15.5893 11.115 17.415 8.19643L17.5629 7.95857C17.8457 7.49571 18 6.96857 18 6.42857C18 5.88857 17.8457 5.36143 17.5629 4.89857L17.415 4.66071ZM16.47 7.27714L16.3221 7.515C14.7343 10.0543 11.9957 11.5714 9 11.5714C6.00429 11.5714 3.26571 10.0543 1.67786 7.515L1.53 7.27714C1.36929 7.02 1.28571 6.73071 1.28571 6.42857C1.28571 6.12643 1.36929 5.83714 1.53 5.58L1.67786 5.34214C3.26571 2.80286 6.00429 1.28571 9 1.28571C11.9957 1.28571 14.7343 2.80286 16.3221 5.34214L16.47 5.58C16.6307 5.83714 16.7143 6.12643 16.7143 6.42857C16.7143 6.73071 16.6307 7.02 16.47 7.27714Z" fill="#ffffff" />
                  <path d="M8.99972 2.57129C6.87186 2.57129 5.14258 4.30057 5.14258 6.42843C5.14258 8.55629 6.87186 10.2856 8.99972 10.2856C11.1276 10.2856 12.8569 8.55629 12.8569 6.42843C12.8569 4.30057 11.1276 2.57129 8.99972 2.57129ZM8.99972 8.99986C7.57901 8.99986 6.42829 7.84915 6.42829 6.42843C6.42829 5.00772 7.57901 3.857 8.99972 3.857C10.4204 3.857 11.5711 5.00772 11.5711 6.42843C11.5711 7.84915 10.4204 8.99986 8.99972 8.99986Z" fill="#ffffff" />
                </svg>
              </IconButton>
            )}

            {hasAction(MODULE_KEY, 'delete') && (
              <IconButton
                size="small"
                onClick={() => handleDeleteClick(params.row.id)}
                sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', width: '36px', height: '36px', display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px solid rgba(227, 214, 245, 0.1)', padding: '0' }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="15" height="16" viewBox="0 0 15 16" fill="none">
                  <path d="M7.21951 1.17073C6.4558 1.17073 5.80457 1.65856 5.56322 2.34141C5.45548 2.64622 5.12105 2.80599 4.81623 2.69825C4.51142 2.59051 4.35166 2.25608 4.4594 1.95127C4.86091 0.81529 5.94427 0 7.21951 0C8.49483 0 9.57815 0.81529 9.97971 1.95127C10.0874 2.25608 9.92765 2.59051 9.62287 2.69825C9.31801 2.80599 8.98357 2.64622 8.87586 2.34141C8.63454 1.65856 7.98322 1.17073 7.21951 1.17073Z" fill="white" />
                  <path d="M0 3.70744C0 3.38415 0.26208 3.12207 0.585366 3.12207H13.8537C14.177 3.12207 14.4391 3.38415 14.4391 3.70744C14.4391 4.03072 14.177 4.2928 13.8537 4.2928H0.585366C0.26208 4.2928 0 4.03072 0 3.70744Z" fill="white" />
                  <path d="M2.47023 5.61969C2.44873 5.29712 2.1698 5.05306 1.84723 5.07456C1.52466 5.09606 1.28059 5.37499 1.3021 5.69757L1.66382 11.1233C1.73054 12.1245 1.78444 12.9332 1.91086 13.5678C2.04229 14.2275 2.26584 14.7786 2.72758 15.2106C3.1893 15.6426 3.75403 15.8289 4.42107 15.9162C5.06266 16.0001 5.87318 16.0001 6.87657 16.0001H7.56246C8.56586 16.0001 9.37632 16.0001 10.018 15.9162C10.685 15.8289 11.2497 15.6426 11.7115 15.2106C12.1732 14.7786 12.3967 14.2275 12.5282 13.5678C12.6545 12.9332 12.7085 12.1245 12.7752 11.1233L13.1369 5.69757C13.1584 5.37499 12.9143 5.09606 12.5918 5.07456C12.2692 5.05306 11.9902 5.29712 11.9688 5.61969L11.6098 11.0043C11.5397 12.0564 11.4896 12.7883 11.38 13.339C11.2735 13.8733 11.125 14.156 10.9116 14.3557C10.6982 14.5553 10.4062 14.6847 9.86607 14.7554C9.30927 14.8282 8.57562 14.8294 7.52133 14.8294H6.9177C5.86342 14.8294 5.12972 14.8282 4.57292 14.7554C4.03282 14.6847 3.7408 14.5553 3.5274 14.3557C3.314 14.156 3.16545 13.8733 3.05903 13.339C2.94932 12.7883 2.89935 12.0564 2.82922 11.0043L2.47023 5.61969Z" fill="white" />
                  <path d="M5.20978 7.02736C5.53147 6.9952 5.81828 7.2299 5.85051 7.55161L6.24076 11.4541C6.27291 11.7757 6.03822 12.0626 5.71651 12.0948C5.39484 12.1269 5.10797 11.8922 5.07581 11.5705L4.68557 7.66806C4.65339 7.34642 4.8881 7.05952 5.20978 7.02736Z" fill="white" />
                  <path d="M9.75346 7.66806C9.78561 7.34642 9.55092 7.05952 9.22921 7.02736C8.90757 6.9952 8.62066 7.2299 8.5885 7.55161L8.19826 11.4541C8.1661 11.7757 8.4008 12.0626 8.72251 12.0948C9.04415 12.1269 9.33098 11.8922 9.36321 11.5705L9.75346 7.66806Z" fill="white" />
                </svg>
              </IconButton>
            )}
          </Box>
        </>
      )
    }
  ];

  const rows = useMemo(() => {
    if (!Array.isArray(audios) || audios.length === 0) {
      return [];
    }

    return audios.map((audio, index) => {
      if (!audio || !audio.id) {
        console.warn('Invalid audio at index', index, audio);
        return null;
      }

      return {
        id: audio.id,
        backendId: audio.id,
        sno: index + 1,
        originalName: audio?.originalName || "",
        usedIn: audio.usedIn || 0,
        durationSeconds: audio.durationSeconds || "N/A",
        fileSize: audio.fileSize || "",
        createdAt: audio.createdAt || "",
        createdById: audio.createdById || null,
        createdByName: audio.createdByName || null,
        status: audio.status || null,
        fileUploadStatus: audio.fileUploadStatus || null,
      };
    }).filter(Boolean); // Remove any null entries
  }, [audios]);

  // if (loading) {
  //     return (
  //         <Box p={4} display="flex" justifyContent="center">
  //             <CircularProgress />
  //         </Box>
  //     );
  // }

  return (
    <Box sx={{ p: '36px 24px 24px' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" sx={{ mb: '24px', flexWrap: 'wrap', columnGap: '6px', rowGap: '16px' }}>
        <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', lineHeight: '32px' }} className='main_title'>{t('audio_library.title', 'Audio Library')}
        </Typography>

        <Box display="flex" gap={2}>
          <TextField
            size="small"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            placeholder={t('search', 'Search')}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: '999px',
                backgroundColor: 'rgba(230, 210, 249, 0.2)',
                color: '#fff',
                height: '38px',
                '& fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                },
                '&:hover fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                },
                '&.Mui-focused fieldset': {
                  borderColor: 'rgba(230, 210, 249, 0.1)',
                  borderWidth: '1px',
                },
              },
              '& .MuiOutlinedInput-input': {
                padding: '10px 16px',
                color: '#FF69B4',
                fontSize: '15px',
                lineHeight: '18px',

              },
              '& input::placeholder': {
                color: 'rgba(255,255,255,0.6)',
                opacity: 1,
              },
              '& .MuiFormHelperText-root': {
                ml: '20px'
              }
            }}
          />
          {hasAction(MODULE_KEY, 'create') && (
            <Button variant="contained" startIcon={<Add />} onClick={() => setModalOpen(true)}
              sx={{
                border: '1px solid rgba(230, 210, 249, 0.1) !important',
                color: '#fff',
                textTransform: 'none',
                backgroundColor: '#FF69B4 !important',
                borderRadius: '999px',
                fontSize: '15px',
                lineHeight: '24px',
                padding: '0',
                minWidth: '38px',
                '& .MuiButton-startIcon': {
                  margin: 0,
                },
              }}
            >
            </Button>
          )}
        </Box>
      </Box>

      {/* DataGrid */}
      <Box sx={{ backgroundColor: 'rgba(255, 255, 255, 0.1)', border: '1px solid rgba(230, 210, 249, 0.2)', borderRadius: '10px', px: '12px' }}>
        <DataGrid
          rows={rows}
          columns={columns}
          loading={loading}
          slots={{ toolbar: GridToolbar, loadingOverlay: CustomLoadingOverlay }}
          paginationMode="server"
          rowCount={pagination?.total || 0}
          paginationModel={paginationModel}
          onPaginationModelChange={(model) => {
            setPaginationModel(model);
          }}
          slotProps={{
            toolbar: {
              csvOptions: { disableToolbarButton: true },
              printOptions: { disableToolbarButton: true },
            },
          }}
          initialState={{
            pagination: { paginationModel: { pageSize: 10 } },
            sorting: { sortModel: [{ field: "nameEn", sort: "asc" }] }
          }}
          getRowId={(row) => row.id || row.sno}
          pageSizeOptions={[10, 25, 50, 100]}
          disableRowSelectionOnClick
          localeText={
            i18n.language?.toLowerCase().startsWith("fr")
              ? frFR.components.MuiDataGrid.defaultProps.localeText
              : enUS.components.MuiDataGrid.defaultProps.localeText
          }
          sx={{
            backgroundColor: 'transparent',
            border: 'none',
            borderRadius: '0',
            '& .MuiDataGrid-toolbarContainer': {
              padding: '12px',
              margin: '0 -12px',
              gap: '10px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
            '& .MuiDataGrid-topContainer::after': {
              display: 'none',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root': {
              color: '#fff',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              borderRadius: '999px',
              textTransform: 'none',
              fontWeight: 500,
              padding: '6px 12px',
              backgroundColor: 'rgba(255,255,255,0.05)',
            },
            '& .MuiDataGrid-toolbarContainer .MuiButton-root .MuiButton-icon .MuiBadge-root svg': {
              width: '18px',
              height: '18px',
            },
            '& .MuiDataGrid-virtualScrollerContent': {
              maxHeight: 'auto',
              height: 'auto !important'
            },
            '& .MuiDataGrid-virtualScroller': {
              borderRadius: '0 !important',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone': {
              position: 'inherit',
            },
            '& .MuiDataGrid-virtualScrollerRenderZone>.MuiDataGrid-row': {
              maxHeight: 'none !important',
              height: 'auto !important',
            },
            '& .MuiDataGrid-cell': {
              height: 'auto !important',
              minHeight: 'var(--height)',
              paddingTop: '5px',
              paddingBottom: '5px',
            },
            '& .MuiDataGrid-cell>.MuiBox-root': {
              minHeight: '100%',
              alignItems: 'center',
            },
            '& .MuiDataGrid-columnHeaders, & .MuiDataGrid-cell': {
              borderRadius: '0 !important',
              borderColor: 'rgba(230, 210, 249, 0.2) !important',
              color: '#fff',
            },
            '& .MuiDataGrid-columnHeaders [role=row]': {
              backgroundColor: '#1a1a1a',
            },
            '& .MuiDataGrid-footerContainer': {
              borderColor: 'rgba(230, 210, 249, 0.2) !important'
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader': {
              color: 'white',
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus, & .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader:focus-within, & .MuiDataGrid-cell:focus, & .MuiDataGrid-cell:focus-within': {
              outline: 'none',
            },
            '& .MuiDataGrid-columnHeaders .MuiDataGrid-columnHeader .MuiSvgIcon-root': {
              opacity: '0.8 !important',
              fill: 'white',
            },
            '& .MuiDataGrid-cell .MuiChip-outlined': {
              border: '0 !important',
              height: 'auto',
              fontWeight: '600',
            },
            '& .MuiDataGrid-cell .MuiChip-outlined.MuiChip-colorSuccess': {
              color: "#05ff27ff",
            },
            '& .MuiTablePagination-root': {
              color: '#fff'
            },
            '& .MuiTablePagination-selectLabel': {
              marginBottom: '0',
            },
            '& .MuiTablePagination-input': {
              marginRight: '10px',
            },
            '& .MuiTablePagination-input .MuiSelect-select': {
              paddingTop: '2px',
              paddingBottom: '2px',
              border: '1px solid rgba(230, 210, 249, 0.2)',
              backgroundColor: 'rgba(255,255,255,0.05)',
              borderRadius: '4px',
            },
            '& .MuiTablePagination-input .MuiSvgIcon-root': {
              fill: 'white'
            },
            '& .MuiTablePagination-toolbar .MuiTablePagination-displayedRows': {
              marginBottom: '0',
            },
            '& .MuiTablePagination-actions': {
              marginLeft: '10px !important',
            },
            '& .MuiTablePagination-actions .MuiButtonBase-root': {
              color: '#FF69B4',
            },
            '& .MuiDataGrid-filler': {
              height: 'auto !important',
            },
            '& .MuiDataGrid-filler>div': {
              borderColor: 'transparent',
            },
            '& .MuiDataGrid-overlayWrapper': {
              height: 'auto !important',
              maxHeight: '50px',
            },
            '& .MuiDataGrid-overlayWrapperInner': {
              minHeight: '40px',
              height: 'auto !important'
            },
            '& .MuiDataGrid-overlay': {
              color: '#fff',
              borderTop: '1px solid rgba(230, 210, 249, 0.2)',
              backgroundColor: 'transparent',
              minHeight: '40px',
            },
          }}
        />
      </Box>

      {/* Upload Dialog */}
      <Dialog open={modalOpen} onClose={() => setModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
          {t('audio_library.popup_titles.upload_audio', "Upload Audio")}
          <IconButton onClick={() => setModalOpen(false)} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            borderColor: 'rgba(230, 210, 249, 0.1)',
            padding: '24px 16px',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(230, 210, 249, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(230, 210, 249, 0.5)',
              },
            },
          }}
        >
          <Box
            onDrop={handleDrop}
            onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDragEnter={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onDragLeave={(e) => { e.preventDefault(); e.stopPropagation(); }}
            onClick={() => fileInputRef.current.click()}
            sx={{
              border: "1px dashed rgba(230, 210, 249, 0.1)",
              borderRadius: "100px",
              height: 120,
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              cursor: "pointer",
              transition: "0.3s",
              padding: '20px'
            }}
          >
            <CloudUpload
              sx={{
                fontSize: 35,
                color: 'white',
              }}
            />

            <Typography variant="body1" sx={{ fontSize: '16px', my: '5px' }}>
              {t('audio_library.popup_titles.drag_drop_here', "Drag & Drop audio files here (Max 10 files at a time)")}
            </Typography>

            <Typography variant="body2" sx={{ fontSize: '14px', color: "rgba(255,255,255,0.6)" }}>
              {t('audio_library.popup_titles.click_browse', "or click to browse (Supported formats: MP3)")}
            </Typography>

            <input
              ref={fileInputRef}
              type="file"
              accept=".mp3,audio/mpeg"
              multiple
              hidden
              onChange={(e) =>
                handleFileSelect(e.target.files)
              }
            />
          </Box>

          {selectedFiles.length > 0 && (
            <Box mt={3}>
              <Typography variant="subtitle1" sx={{ fontSize: '16px', color: 'white', fontWeight: '400', mb: '5px' }}>
                {t('audio_library.popup_titles.selected_files', "Selected Files")}
              </Typography>

              {selectedFiles.map((file, index) => {
                const progress = uploadProgress[file.name] || 0;

                return (
                  <Box
                    key={index}
                    sx={{
                      p: 1.5,
                      mb: 1,
                      border: "1px solid rgba(230, 210, 249, 0.1)",
                      borderRadius: "8px",
                      position: "relative",
                      overflow: "hidden",
                    }}
                  >
                    {/* Progress Background */}
                    {progress > 0 && (
                      <Box
                        sx={{
                          position: "absolute",
                          left: 0,
                          top: 0,
                          height: "100%",
                          width: `${progress}%`,
                          background:
                            "linear-gradient(90deg, rgba(161,37,235,0.25), rgba(253,86,201,0.25))",
                          transition: "width 0.3s",
                        }}
                      />
                    )}

                    <Box
                      display="flex"
                      justifyContent="space-between"
                      alignItems="center"
                      sx={{ position: "relative", zIndex: 1 }}
                    >
                      <Box>
                        <Typography variant="body2">
                          {file.name}
                        </Typography>

                        <Typography
                          variant="caption"
                          sx={{ color: "rgba(255,255,255,0.6)", display: "block" }}
                        >
                          {(file.size / (1024 * 1024)).toFixed(2)} MB
                        </Typography>

                        {/* Percentage */}
                        {progress > 0 && (
                          <Typography
                            variant="caption"
                            sx={{ color: "#FF69B4", fontWeight: 600 }}
                          >
                            {progress} %
                          </Typography>
                        )}
                      </Box>

                      <IconButton
                        color="error"
                        disabled={
                          progress > 0 && progress < 100
                        }
                        onClick={() => handleRemoveFile(index)}
                      >
                        <Close />
                      </IconButton>
                    </Box>

                    {/* Audio Preview */}
                    {file.preview && (
                      <Box
                        mt={0.5}
                        sx={{
                          background: "#121212",
                          borderRadius: "100px",
                          padding: "5px 5px 0 5px",
                          border: "1px solid rgba(230, 210, 249, 0.1)",
                        }}
                      >
                        <audio
                          controls
                          preload="metadata"
                          style={{
                            width: "100%",
                            filter: "invert(1) hue-rotate(180deg)",
                          }}
                        >
                          <source
                            src={file.preview}
                            type={file.type || "audio/mp3"}
                          />
                          Your browser does not support the audio element.
                        </audio>
                      </Box>
                    )}
                  </Box>
                );
              })}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
          <Button onClick={() => setModalOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>
            {t('cancel', 'Cancel')}
          </Button>
          {selectedFiles.length > 0 && (
            <Button
              disabled={isUploading}
              variant="contained"
              onClick={handleSubmitForm}
              sx={{
                py: 1.5,
                borderRadius: '999px',
                fontWeight: '400',
                fontSize: '15px',
                lineHeight: '20px',
                background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
                textTransform: 'capitalize',
                minWidth: { xs: '100%', sm: '150px' },
                marginLeft: '10px',
              }}
            >
              {isUploading ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                t('save', "Save")
              )}
            </Button>
          )}
        </DialogActions>
      </Dialog>

      {/* Delete Album  */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', textAlign: 'center' }}>{t("audio_library.popup_titles.confirm_delete", "Confirm Delete")}</DialogTitle>
        <DialogContent sx={{ pb: '0' }}>
          <Typography sx={{ fontSize: '15px', fontWeight: '400', textAlign: 'center' }}>{t("audio_library.messages.delete_audio_confirmation", "Are you sure you want to delete this audio?")}</Typography>
        </DialogContent>
        <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', justifyContent: 'center', gap: '6px' }}>
          <Button onClick={() => setDeleteDialogOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}> {t("cancel", "Cancel")} </Button>
          <Button
            variant="contained"
            disabled={deleting}
            onClick={handleDeleteConfirm}
            sx={{
              py: 1.5,
              borderRadius: '999px',
              fontWeight: '400',
              fontSize: '15px',
              lineHeight: '20px',
              background: 'linear-gradient(90deg, rgba(161, 37, 235, 1) 0%, rgba(253, 86, 201, 1) 100%)',
              textTransform: 'capitalize',
              minWidth: { xs: '100%', sm: '150px' },
              marginLeft: '10px',
            }}
          >
            {deleting ? <CircularProgress size={20} sx={{ color: 'white' }} /> : t("delete", "Delete")}
          </Button>
        </DialogActions>
      </Dialog>
      {/* End of Delete Album  */}

      {/* View Album Dialog */}
      <Dialog open={viewModalOpen} onClose={() => setViewModalOpen(false)} fullWidth maxWidth="sm">
        <DialogTitle sx={{ padding: '16px', fontSize: '18px', }}>
          {t('audio_library.popup_titles.view_audio', "View Audio")}
          <IconButton onClick={() => setViewModalOpen(false)} sx={{ position: "absolute", right: '16px', top: '20px', padding: '0', color: 'white' }}>
            <Close />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            borderColor: 'rgba(230, 210, 249, 0.1)',
            padding: '24px 16px',
            '&::-webkit-scrollbar': {
              width: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(230, 210, 249, 0.3)',
              borderRadius: '4px',
              '&:hover': {
                background: 'rgba(230, 210, 249, 0.5)',
              },
            },
          }}
        >
          {selectedAudio ? (
            <>
              <Typography variant="h6" gutterBottom sx={{ fontSize: '18px', mb: '10px' }}>
                {selectedAudio.originalName}
              </Typography>

              <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                {t('audio_library.fields.duration', "Duration")}:{" "}
                {formatDuration(selectedAudio.durationSeconds)}
              </Typography>

              <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                {t('audio_library.fields.file_size', "File Size")}:{" "}
                {(selectedAudio.fileSize / (1024 * 1024)).toFixed(2)} MB
              </Typography>

              <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                {t('audio_library.fields.uploaded_by', "Uploaded By")}: {selectedAudio.createdBy}
              </Typography>

              <Typography variant="body2" gutterBottom sx={{ fontSize: '14px', mb: '5px' }}>
                {t('audio_library.fields.upload_date', "Uploaded At")}: {formatUTCDateForDataGrid(selectedAudio.createdAt)}
              </Typography>

              {/* Audio Player */}
              {audioPreview && (
                <Box
                  mt={3}
                  sx={{
                    background: "#121212",
                    borderRadius: "100px",
                    padding: "5px 5px 0 5px",
                    border: "1px solid rgba(230, 210, 249, 0.1)",
                  }}
                >
                  <audio
                    controls
                    preload="metadata"
                    style={{
                      width: "100%",
                      filter: "invert(1) hue-rotate(180deg)", // makes controls appear dark-friendly
                    }}
                  >
                    <source
                      src={audioPreview}
                      type="audio/mp3"
                    />
                    Your browser does not support the audio element.
                  </audio>
                </Box>
              )}
            </>
          ) : (
            <Box display="flex" justifyContent="center" alignItems="center" p={4} minHeight={200}>
              <CircularProgress sx={{ color: '#FF69B4' }} />
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ px: '16px', py: '20px', flexWrap: 'wrap', gap: '6px' }}>
          <Button onClick={() => setViewModalOpen(false)} sx={{ backgroundColor: 'rgba(227, 214, 245, 0.2) !important', border: '1px solid rgba(227, 214, 245, 0.1)', fontSize: '15px', lineHeight: '20px', fontWeight: '400', padding: '11px 20px', minWidth: { xs: '100%', sm: '150px' }, color: 'white', borderRadius: '999px', textTransform: 'capitalize' }}>{t('close', 'Close')}</Button>
        </DialogActions>
      </Dialog>

      <GlobalStyles
        styles={{
          '.MuiDataGrid-panel .MuiDataGrid-paper,.MuiPopover-root .MuiPopover-paper, .base-Popper-root .MuiPaper-root, .MuiDialog-root .MuiPaper-root': {
            backgroundColor: '#252525 !important',
            border: '1px solid rgba(230, 210, 249, 0.1)',
            fontSize: '15px',
            color: '#fff',
            borderRadius: '4px',
          },
          '.MuiDialog-root .MuiPaper-root': {
            borderRadius: '20px',
          },
          '.MuiDataGrid-columnsManagementHeader': {
            padding: '10px !important',
          },
          '.MuiInputBase-formControl': {
            fontSize: '14px !important',
            color: 'white !important',
            borderRadius: '999px !important',
          },
          '.MuiInputBase-formControl .MuiOutlinedInput-notchedOutline': {
            border: '1px solid rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiInputBase-formControl .MuiInputAdornment-positionStart svg': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter': {
            borderTop: '1px solid rgba(230, 210, 249, 0.1)',
            padding: '4px 8px 4px 16px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement': {
            padding: '0px 16px 12px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiFormControlLabel-label,.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiTypography-root': {
            fontSize: '14px',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagement .MuiFormControlLabel-root .MuiCheckbox-root .MuiSvgIcon-root, .MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-columnsManagementFooter .MuiButtonBase-root': {
            color: '#FF69B4',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiDataGrid-filterFormDeleteIcon .MuiSvgIcon-root,.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root svg,.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiFormLabel-root': {
            color: 'white',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:before': {
            borderColor: 'rgba(230, 210, 249, 0.1) !important',
          },
          '.MuiDataGrid-paper .MuiDataGrid-filterForm .MuiFormControl-root .MuiInputBase-root:after': {
            display: 'none',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root, .base-Popper-root .MuiPaper-root .MuiList-root': {
            padding: '0',
          },
          '.MuiPopover-root .MuiPopover-paper .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root, .base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiTypography-root': {
            fontSize: '14px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root': {
            padding: '5px 10px',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root.Mui-selected .MuiListItemIcon-root .MuiSvgIcon-root': {
            fill: '#FF69B4',
          },
          '.base-Popper-root .MuiPaper-root .MuiDivider-root': {
            borderColor: 'rgba(230, 210, 249, 0.3) !important',
            margin: '0',
          },
          '.base-Popper-root .MuiPaper-root .MuiList-root .MuiButtonBase-root .MuiListItemIcon-root .MuiSvgIcon-root': {
            minWidth: '32px',
          },
          '.MuiDataGrid-scrollbarFiller': {
            borderTop: 'none !important',
          },
          '& .MuiDataGrid-scrollbar': {
            '&::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-track': {
              background: 'rgba(255, 255, 255, 0.05)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb': {
              background: 'rgba(230, 210, 249, 0.3)',
              borderRadius: '4px',
            },
            '&::-webkit-scrollbar-thumb:hover': {
              background: 'rgba(230, 210, 249, 0.5)',
            },
          },
        }}
      />
    </Box>
  );
}

export default AudioLibrary;
